import { useState } from 'react';
import { CheckCircle } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { SCRAP_REASONS } from './mfgApi';

// 完了時の良品数/不良数(+理由)キャプチャ — OEE 品質 & 不良率 の入力。
// variant: 'panel' (WO詳細内インライン) | 'kiosk' (タッチ大ボタン)
export default function CompleteCapture({ plannedQty = 0, uom = '', busy = false, onSubmit, onCancel, variant = 'panel' }) {
  const { t } = useT();
  const [good, setGood] = useState(String(plannedQty ?? 0));
  const [scrap, setScrap] = useState('0');
  const [reason, setReason] = useState('dimension');
  const [note, setNote] = useState('');

  const g = Number(good) || 0;
  const s = Number(scrap) || 0;
  const submit = () => onSubmit({
    good_qty: g, scrap_qty: s,
    scrap_reason: s > 0 ? reason : null,
    scrap_note: s > 0 ? (note || null) : null,
  });

  const kiosk = variant === 'kiosk';
  const inputCls = kiosk ? 'h-16 text-2xl text-center font-bold bg-white text-zinc-900' : 'h-10 mt-1';
  const labelCls = kiosk ? 'text-sm font-semibold text-emerald-900' : 'text-xs font-semibold text-zinc-600';

  return (
    <div
      data-testid="complete-capture"
      className={kiosk
        ? 'rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-4'
        : 'rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3'}
    >
      <p className={kiosk ? 'text-base text-emerald-900 font-bold' : 'text-sm text-emerald-900 font-medium'}>
        {t('mfg.wo.completeCapture')} <span className="text-emerald-700">({t('mfg.wo.planned')}: {plannedQty} {uom})</span>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={labelCls}>{t('mfg.wo.goodQty')}</Label>
          <Input type="number" min="0" value={good} onChange={(e) => setGood(e.target.value)} className={inputCls} data-testid="complete-good-input" />
        </div>
        <div>
          <Label className={labelCls}>{t('mfg.wo.scrapQty')}</Label>
          <Input type="number" min="0" value={scrap} onChange={(e) => setScrap(e.target.value)} className={inputCls} data-testid="complete-scrap-input" />
        </div>
      </div>

      {s > 0 && (
        <div className="space-y-2" data-testid="complete-scrap-detail">
          <Label className={labelCls}>{t('mfg.wo.scrapReason')}</Label>
          {kiosk ? (
            <div className="grid grid-cols-3 gap-2">
              {SCRAP_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)} data-testid={`complete-reason-${r}`}
                  className={`h-12 rounded-xl text-sm font-semibold border-2 transition-colors ${reason === r ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-700 border-zinc-200'}`}>
                  {t(`mfg.scrapReason.${r}`)}
                </button>
              ))}
            </div>
          ) : (
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-10" data-testid="complete-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SCRAP_REASONS.map((r) => <SelectItem key={r} value={r}>{t(`mfg.scrapReason.${r}`)}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('mfg.wo.scrapNotePh')} className={kiosk ? 'h-12 bg-white' : 'h-9'} data-testid="complete-scrap-note" />
        </div>
      )}

      <div className={kiosk ? 'grid grid-cols-2 gap-3' : 'flex justify-end gap-2'}>
        <button type="button" onClick={onCancel} disabled={busy}
          className={kiosk ? 'h-14 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-semibold' : 'px-3 h-9 rounded-lg text-sm text-zinc-600 hover:bg-zinc-100'}>
          {t('mfg.cancel')}
        </button>
        <button type="button" onClick={submit} disabled={busy} data-testid="complete-confirm-btn"
          className={kiosk
            ? 'h-14 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg flex items-center justify-center gap-2'
            : 'px-4 h-9 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold flex items-center gap-1.5'}>
          <CheckCircle size={kiosk ? 22 : 15} weight="fill" /> {t('mfg.wo.completeDo')}
        </button>
      </div>
    </div>
  );
}
